import { create } from 'zustand';
import { supabase } from '../lib/supabase';

const API_BASE = import.meta.env.VITE_API_BASE_PATH || '/api/v1/ai-coe';
const STORAGE_KEY = 'aicoe-auth';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

function getTokenFromStorage(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.access_token || null;
  } catch {
    return null;
  }
}

async function fetchMe(token: string): Promise<User | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.user;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,

  initialize: async () => {
    // Detect an in-progress OAuth callback (PKCE returns ?code=..., implicit returns
    // #access_token=...). Right after the Google redirect the token isn't in
    // localStorage yet — Supabase still needs to exchange the code — so we must wait
    // for the auth event instead of immediately deciding the user is logged out.
    const url = new URL(window.location.href);
    const isOAuthCallback =
      url.searchParams.has('code') || window.location.hash.includes('access_token');

    // Listen for auth changes (sign in/out, token refresh, and the post-OAuth session).
    supabase.auth.onAuthStateChange(async (event, session) => {
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        const u = await fetchMe(session.access_token);
        if (u) {
          set({ user: u, loading: false });
          // Clean the OAuth params out of the URL after a successful exchange.
          if (url.searchParams.has('code')) {
            window.history.replaceState({}, document.title, url.pathname);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        set({ user: null, loading: false });
      } else if (event === 'TOKEN_REFRESHED' && session) {
        const stored = getTokenFromStorage();
        if (!stored) set({ user: null, loading: false });
      }
    });

    if (isOAuthCallback) {
      // Let Supabase finish the code exchange; the listener above resolves the user.
      // Safety net: if nothing arrives in time, stop the spinner so the user can retry.
      set({ loading: true });
      setTimeout(() => {
        if (useAuthStore.getState().loading) {
          set({ loading: false });
        }
      }, 8000);
      return;
    }

    // Normal load — read token directly from localStorage (no Supabase client call,
    // which avoids the navigator.locks hang after tab inactivity).
    const token = getTokenFromStorage();

    if (!token) {
      set({ user: null, loading: false });
      return;
    }

    // Fetch user profile from our backend
    const user = await fetchMe(token);

    if (user) {
      set({ user, loading: false });
    } else {
      // Token might be expired — try refresh
      try {
        const { data } = await supabase.auth.refreshSession();
        if (data.session?.access_token) {
          const refreshedUser = await fetchMe(data.session.access_token);
          set({ user: refreshedUser, loading: false });
        } else {
          set({ user: null, loading: false });
        }
      } catch {
        set({ user: null, loading: false });
      }
    }
  },

  signInWithGoogle: async () => {
    set({ error: null });
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        queryParams: { hd: 'iqm.com' },
      },
    });
    if (error) {
      set({ error: error.message });
    }
  },

  signOut: async () => {
    await supabase.auth.signOut();
    localStorage.removeItem(STORAGE_KEY);
    set({ user: null });
  },
}));
